import { useEffect, useRef } from "react";
import androidLogo from "../../assets/android-logo.png";
import { lockDocumentScroll } from "../../utils/scrollLock";
import { focusFirstElement, trapFocus } from "../../utils/focusTrap";

function DownloadArrow() {
  return (
    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v12m0 0l-4-4m4 4l4-4" />
    </svg>
  );
}

export function ApkModal({ isOpen, onClose, version, url, sha256, signerSha256 }) {
  const panelRef = useRef(null);
  const lastFocusedRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    lastFocusedRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const unlock = lockDocumentScroll();
    const frame = window.requestAnimationFrame(() => focusFirstElement(panelRef.current));

    const onKey = (e) => {
      if (e.key === "Escape") { onClose(); return; }
      trapFocus(panelRef.current, e);
    };

    window.addEventListener("keydown", onKey);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("keydown", onKey);
      unlock();
      lastFocusedRef.current?.focus?.();
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const hasRelease = version && url;

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center p-4"
      style={{
        background: "rgba(var(--page-bg-rgb), 0.80)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
      }}
      onClick={onClose}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Download BiorVault APK"
        tabIndex="-1"
        className="relative w-full max-w-sm rounded-3xl text-center"
        style={{
          background: "linear-gradient(160deg, rgba(30,25,68,0.98), rgba(14,13,41,0.99))",
          border: "1px solid rgba(255,255,255,0.09)",
          boxShadow: "0 40px 100px rgba(4,4,28,0.60), 0 0 0 1px rgba(87,88,252,0.08)",
          backdropFilter: "blur(24px)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Accent glow */}
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-px rounded-full"
          style={{ background: "linear-gradient(90deg, transparent, rgba(87,88,252,0.55), transparent)" }}
        />

        {/* Close button */}
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute top-4 right-4 flex items-center justify-center rounded-full transition-colors"
          style={{
            width: "2rem",
            height: "2rem",
            border: "1px solid rgba(255,255,255,0.10)",
            backgroundColor: "rgba(255,255,255,0.04)",
            color: "rgba(255,255,255,0.38)",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = "rgba(255,255,255,0.75)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = "rgba(255,255,255,0.38)"; }}
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="px-8 pt-8 pb-7">

          {/* Android icon */}
          <div
            className="mx-auto mb-5 flex items-center justify-center rounded-2xl"
            style={{
              width: "3.5rem",
              height: "3.5rem",
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.09)",
            }}
          >
            <img src={androidLogo} alt="" className="w-7 h-7 object-contain" />
          </div>

          {/* Title */}
          <h2
            className="font-syne font-bold text-white"
            style={{ fontSize: "1.3rem", lineHeight: "1.15" }}
          >
            BiorVault for Android
          </h2>

          {/* Version badge */}
          <div
            className="mt-3 inline-flex items-center gap-2 rounded-full px-3 py-1.5"
            style={{
              border: "1px solid rgba(255,255,255,0.09)",
              backgroundColor: "rgba(255,255,255,0.04)",
            }}
          >
            <span
              className="block rounded-full"
              style={{
                width: "0.45rem",
                height: "0.45rem",
                backgroundColor: hasRelease ? "#2bd56f" : "rgba(255,255,255,0.2)",
                boxShadow: hasRelease ? "0 0 6px rgba(43,213,111,0.6)" : "none",
              }}
            />
            <span className="font-dm text-xs" style={{ color: "rgba(255,255,255,0.52)" }}>
              {version ? `Version ${version}` : "Loading…"}
            </span>
          </div>

          {/* Sideload note */}
          <p
            className="mt-4 font-dm text-sm leading-relaxed"
            style={{ color: "rgba(255,255,255,0.36)" }}
          >
            Enable{" "}
            <span style={{ color: "rgba(255,255,255,0.58)", fontWeight: 500 }}>"Install unknown apps"</span>
            {" "}in Android settings before installing.
          </p>

          {/* Integrity: what to compare the downloaded file against */}
          {hasRelease ? (
            <div
              className="mt-4 rounded-xl font-dm text-xs leading-relaxed"
              style={{
                padding: "0.75rem 0.875rem",
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
                color: "rgba(255,255,255,0.52)",
              }}
            >
              {sha256 ? (
                <>
                  <p style={{ color: "rgba(255,255,255,0.7)", fontWeight: 500 }}>SHA-256 of the APK</p>
                  <code className="block break-all" style={{ color: "rgba(255,255,255,0.58)" }}>{sha256}</code>
                  <p className="mt-2">
                    Compare with <code>sha256sum BiorVault-v{version}.apk</code> before installing. A different value means a different file.
                  </p>
                </>
              ) : (
                <p>
                  No checksum is published for this build yet. Only install a file you downloaded from this page.
                </p>
              )}
              {signerSha256 ? (
                <p className="mt-2 break-all">
                  Signing certificate SHA-256: <code style={{ color: "rgba(255,255,255,0.58)" }}>{signerSha256}</code>
                </p>
              ) : null}
            </div>
          ) : null}

          {/* Download button */}
          {hasRelease ? (
            <a
              href={url}
              download={`BiorVault-v${version}.apk`}
              className="mt-5 flex w-full items-center justify-center gap-2.5 rounded-xl font-dm font-semibold text-white transition-opacity hover:opacity-90"
              style={{
                padding: "0.875rem 1.25rem",
                fontSize: "0.9rem",
                background: "linear-gradient(135deg, #5758fc, #3738c4)",
                boxShadow: "0 8px 28px rgba(87,88,252,0.32)",
              }}
              onClick={onClose}
            >
              <DownloadArrow />
              Download APK — v{version}
            </a>
          ) : (
            <div
              className="mt-5 flex w-full items-center justify-center rounded-xl font-dm text-sm"
              style={{
                padding: "0.875rem 1.25rem",
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
                color: "rgba(255,255,255,0.30)",
              }}
            >
              {version === null ? "Loading…" : "APK unavailable"}
            </div>
          )}

          {/* Dismiss */}
          <button
            type="button"
            onClick={onClose}
            className="mt-3 w-full py-1.5 font-dm text-xs transition-colors"
            style={{ color: "rgba(255,255,255,0.25)" }}
            onMouseEnter={(e) => { e.currentTarget.style.color = "rgba(255,255,255,0.50)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = "rgba(255,255,255,0.25)"; }}
          >
            Cancel
          </button>

        </div>
      </div>
    </div>
  );
}

export default ApkModal;
